import React from 'react'

import {groupBy, sortBy} from 'es-toolkit'
import invariant from 'invariant'

import {useAppDispatch, useAppSelector} from '../hooks/redux'
import {fatalError} from '../redux/features/core'
import {openEditDiscountsModal} from '../redux/features/modal'

import useAsync from '../../../commons/src/hooks/use-async'
import {formatPercentage, formatPrice} from '../libs/formatter'
import * as discountApi from '../services/api/discount'

import {DiscountId, DiscountType} from '../types'

import Button from '../../../commons/src/components/button'
import Headline from '../../../commons/src/components/headline'
import Container from '../components/container'
import PageContentLayout from '../components/page-content-layout'
import PageHeader from '../components/page-content-layout/PageHeader'
import Table from '../components/table'
import TableCell from '../components/table/TableCell'
import TableHeaderCell from '../components/table/TableHeaderCell'
import TableRow from '../components/table/TableRow'

import PagePartial from './page-partial'

type TableSubRowContent = {
  id: DiscountId
  threshold: string
  discount: string
}

type TableRowContent = {
  type: DiscountType
  label: string
  discounts: TableSubRowContent[]
}

const discountTypeNameMap: Record<DiscountType, string> = {
  [DiscountType.AMOUNT]: 'Value',
  [DiscountType.QUANTITY]: 'Quantity',
}

const Discounts = () => {
  const dispatch = useAppDispatch()

  const provider = useAppSelector(state => state.core.provider)

  invariant(provider, 'Provider is missing.')

  const {
    isLoading,
    data: discounts = [],
    refresh,
  } = useAsync(() => discountApi.getByProvider(provider.id), [], {
    onError: error => dispatch(fatalError({error})),
    defaultLoading: true,
  })

  const discountTypes = Object.values(DiscountType)
  const discountsByType = React.useMemo(
    () => groupBy(discounts, discount => discount.type),
    [discounts]
  )

  const formatThreshold = (type: DiscountType, threshold: number) =>
    type === DiscountType.QUANTITY
      ? `≥ ${threshold} pcs.`
      : `≥ ${formatPrice(threshold, provider.currency)}`

  const tableRows: TableRowContent[] = discountTypes.map(key => ({
    type: key,
    label: discountTypeNameMap[key],
    discounts: discountsByType[key]
      ? sortBy(discountsByType[key], ['threshold']).map(({id, type, threshold, discount}) => ({
          id,
          threshold: formatThreshold(type, threshold),
          discount: formatPercentage(discount),
        }))
      : [],
  }))

  const renderSubRow = ({id, threshold, discount}: TableSubRowContent) => (
    <TableRow key={id}>
      <TableCell />
      <TableCell>{threshold}</TableCell>
      <TableCell strong minor>
        {discount}
      </TableCell>
      <TableCell />
    </TableRow>
  )

  const renderTableRow = ({type, label, discounts: subRows}: TableRowContent) => {
    const hasSubRows = subRows.length > 0
    const firstSubRow = subRows[0]
    const otherSubRows = subRows.slice(1)

    return (
      <TableRow key={type} subRows={otherSubRows.map(renderSubRow)}>
        <TableCell strong>{label}</TableCell>
        <TableCell>{firstSubRow && firstSubRow.threshold}</TableCell>
        <TableCell strong minor>
          {firstSubRow && firstSubRow.discount}
        </TableCell>
        <TableCell>
          <Button
            hierarchy="secondary"
            onClick={() =>
              dispatch(
                openEditDiscountsModal({type, discounts: discountsByType[type], onDone: refresh})
              )
            }
          >
            {hasSubRows ? 'Edit' : 'Add'}
          </Button>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <PagePartial>
      <PageContentLayout header={<PageHeader headline={<Headline size="l" label="Discounts" />} />}>
        <Container>
          <Table
            isLoading={isLoading}
            empty="No discounts have been added yet."
            header={
              <TableRow>
                <TableHeaderCell width={1}>Type</TableHeaderCell>
                <TableHeaderCell width={1} align="right">
                  Threshold
                </TableHeaderCell>
                <TableHeaderCell width={1} align="right">
                  Discount
                </TableHeaderCell>
                <TableHeaderCell width={4} align="right" />
              </TableRow>
            }
          >
            {tableRows.map(renderTableRow)}
          </Table>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}

export default Discounts
